import { Component, EventEmitter, inject, Input, OnDestroy, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { BraintreeService } from '../../data-access/braintree.service';
import { Subject, takeUntil } from 'rxjs';
import * as braintree from 'braintree-web';
import { PaymentMethodAddCreditCardDialogComponent } from '../../ui/payment-method-add-credit-card-dialog/payment-method-add-credit-card-dialog.component';
import { PaymentDonationsService } from '../../data-access/payment-donations.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PaymentMethodAddPaypalComponent } from '../../ui/payment-method-add-paypal/payment-method-add-paypal.component';
import { BtAchBankAccountDialogComponent } from '../../ui/bt-ach-bank-account-dialog/bt-ach-bank-account-dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'equine-ui-nx-donation-bt',
  standalone: true,
  imports: [
    CommonModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './donation-bt.component.html',
  styleUrl: './donation-bt.component.scss',
})
export class DonationBtComponent implements OnDestroy {

  private braintreeService = inject(BraintreeService);
  private unsubscribe$ = new Subject<void>();
  private clientInstance!: braintree.Client;
  private dataCollectorInstance!: braintree.DataCollector;
  private customerId!: string;

  private dialog = inject(MatDialog);
  private paymentDonationsService = inject(PaymentDonationsService);
  private _snackBar = inject(MatSnackBar);

  private bankInstance!: braintree.USBankAccount;

  @Input() donation: any;
  @Output() succeeded = new EventEmitter<any>();
  @Output() failed = new EventEmitter<any>();

  loading = signal(false);

  //1. create customer

  paymentMethod = new FormControl(null);
  
  openPaymentMethodDialog() {
    if (!this.paymentMethod.value) {
      return;
    }

    this.loading.set(true);

    switch(this.paymentMethod.value) {
      case 'credit_card': {
        this.getOrCreateCustomer().then(() => {
          this.openCreditCardDialog();
        });
        break;
      }

      case 'paypal_account': {
        this.getOrCreateCustomer().then(() => {
          this.openPaypalDialog();
        });
        break;
      }

      case 'us_bank_account': {
        this.getOrCreateCustomer().then(() => {
          this.setUsBankAccountInstance().then(() => {
            this.openBankDialog();
          })
        });
        break;
      }

    }
  }

  getOrCreateCustomer(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.donation.loggedInUserId) {
        if (this.clientInstance) {
          return resolve();
        }

        this.braintreeService.getBraintreeTokenAndCustomerData(this.donation.loggedInUserId)
          .pipe(
                takeUntil(this.unsubscribe$)
              )
              .subscribe({
                next: (response) => {        
                  braintree.client.create({authorization: response.clientToken})
                    .then(clientInstance => {
                      this.clientInstance = clientInstance;
                      this.customerId = response.btCustomer.id as string;
                      this.setDataCollector();
                      resolve();
                    })
                    .catch(() => {
                      resolve();
                    });
                },
                error: (err) => {
                  reject();
                }
              })
      } else {

      }
    });


    
  }

  setDataCollector() {
    braintree.dataCollector.create({
      client: this.clientInstance
    }).then((dataCollectorInstance) => {
      this.dataCollectorInstance = dataCollectorInstance;
    })
    .catch((err) => {
      void 0;
    }); 
  }

  openCreditCardDialog() {
    const dialog = this.dialog.open(PaymentMethodAddCreditCardDialogComponent, {
      disableClose: true,
      data: {
        clientInstance: this.clientInstance,
        overrideButtonLabel: 'Donate'
      }
    });

    dialog.afterClosed().subscribe({
      next: () => {
        this.loading.set(false);
      }
    });

    dialog.componentInstance.creditCardTokenized
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next:  (ccToken: {nonce: string}) => {
          this.processPayment(ccToken, 'credit_card').pipe(
            takeUntil(this.unsubscribe$)
          )
          .subscribe({
            next: (response) => {
              this.succeeded.emit(response);
              dialog.close(response);
            },
            error: (error: any) => {
              dialog.componentInstance.paying.set(false);
              this._snackBar.open(error?.error?.message || 'An error occured with your payment. Please try again.', 'Dismiss')
            }
          })
        }
      });
  }

  openPaypalDialog() {
    const dialog = this.dialog.open(PaymentMethodAddPaypalComponent, {
      disableClose: true,
      data: {
        clientInstance: this.clientInstance,
        type: this.donation.type,
        amount: this.getAmountFromDonation(this.donation)
      }
    });

    dialog.afterClosed().subscribe({
      next: () => {
        this.loading.set(false);
      }
    });

    dialog.componentInstance.paypalTokenized
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next:  (paypalToken: {nonce: string}) => {
          this.processPayment(paypalToken, 'paypal_account').pipe(
            takeUntil(this.unsubscribe$)
          )
          .subscribe({
            next: (response) => {
              this.succeeded.emit(response);
              dialog.close(response);
            },
            error: (error: any) => {
              dialog.componentInstance.paying.set(false);
              dialog.componentInstance.loading.set(false);
              this._snackBar.open(error?.error?.message || 'An error occured with your payment. Please try again.', 'Dismiss')
            }
          })
        }
      });
  }

  setUsBankAccountInstance(): Promise<void> {
    return new Promise((resolve, reject) => {
      braintree.usBankAccount.create({
        client: this.clientInstance
      })
        .then((usBankAccountInstance: braintree.USBankAccount) => {
          this.bankInstance = usBankAccountInstance;
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    })
    
  }

  openBankDialog() {
    const dialogRef = this.dialog.open(BtAchBankAccountDialogComponent, {
      disableClose: true
    });

    dialogRef.afterClosed().subscribe({
      next: () => {
        this.loading.set(false);
      }
    });
    
    dialogRef.componentInstance.formSubmit.asObservable()
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (formData: {bankDetails: any, mandateText: string}) => {
          dialogRef.componentInstance.isLoading.set(true);
          dialogRef.componentInstance.error.set(null);

          this.bankInstance.tokenize({
            bankDetails: formData.bankDetails,
            mandateText: formData.mandateText,
            bankLogin: null
          }, (tokenizeErr, tokenizedPayload: {description: string, details: any, nonce: string, type: string}) => {
            if (tokenizeErr) {
              console.error('There was an error tokenizing the bank details.');
              throw tokenizeErr;
            }

            this.processPayment(tokenizedPayload, 'us_bank_account')
              .pipe(
                takeUntil(this.unsubscribe$)
              )
              .subscribe({
                next: (response) => {
                  this.succeeded.emit(response);
                  dialogRef.close(response);
                },
                error: (error) => {
                  debugger
                  dialogRef.componentInstance.isLoading.set(false);
                  dialogRef.componentInstance.error.set(error.error.message);
                  dialogRef.componentInstance.authorizationControl.reset();
                }
              })
            
            console.log(tokenizedPayload);
          });

        }
      });
  }

  getAmountFromDonation(donation: any): number {
    if (donation.planCode === "OTHER_AMOUNT") {
      if (donation.coverProcessingFees) {
        return donation.otherAmount * 1.03;
      } else {
        return donation.otherAmount as number;
      }
    } else {
      switch(donation.planCode) {
        case 'PLAN_25': {
          if (donation.coverProcessingFees) {
            return 25*1.03;
          } else {
            return 25;
          }
        }
        case 'PLAN_50': {
          if (donation.coverProcessingFees) {
            return 50*1.03;
          } else {
            return 50;
          }
        }
        case 'PLAN_100': {
          if (donation.coverProcessingFees) {
            return 100*1.03;
          } else {
            return 100;
          }
        }
        case 'PLAN_250': {
          if (donation.coverProcessingFees) {
            return 250*1.03;
          } else {
            return 250;
          }
        }
        default: {
          return 0;
        }
      }
    }
  }

  processPayment(token: {nonce: string}, type: string) {
    return this.paymentDonationsService.payDonationWithBraintree(
      this.donation._id,
      this.donation.frequency || 'UNIQUE',
      this.donation.planCode,
      this.donation.loggedInUserId,
      this.donation.otherAmount || 0,
      this.donation.coverProcessingFees,
      this.customerId,
      token.nonce,
      this.dataCollectorInstance.deviceData,
      type
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
